import React, { FC } from 'react'
import Icon from '@ant-design/icons'
import { Flex, Skeleton } from 'antd'
import { EmptyState, Well } from '@pushly/aqe/lib/components'
import { useService } from '@pushly/aqe/lib/hooks/use-service'
import { domainHasFlag } from '../constants'
import { BrandVoiceWell } from './brand-voice-well'
import { BRAND_VOICE_DOCS_URL } from './brand-voice-help-content'
import BrandVoiceFilled from '../../icons/brand-voice-filled'
import { FEAT_PUSHLY_AI } from '../../../constants'
import { AbilityAction } from '../../../enums/ability-action.enum'
import { AppState } from '../../../stores/app'
import { DomainDto } from '../../../dtos/domain'
import { VERTICAL_WELL_GAP } from '../../../theme/constants'
import { getClassNames } from '../../../_utils/classnames'

interface IBrandVoiceSettings {
    loading: boolean
    domain?: DomainDto
}

/**
 * What a domain without Pushly AI sees: what Brand Voice is, and that Pushly AI makes it available. There is no
 * switch here. Brand Voice has no flag of its own, and a switch on this page would read as controlling the whole of
 * Pushly AI from one of its parts.
 */
const BrandVoiceNeedsPushlyAi: FC<{ isInternalUser: boolean }> = ({ isInternalUser }) => (
    <Well title="Brand Voice" footerMode="hidden">
        <EmptyState
            icon={<Icon component={BrandVoiceFilled} />}
            iconVariant="chip"
            title="Brand Voice comes with Pushly AI"
            description={
                <>
                    {'Brand Voice holds everything Pushly AI writes for this domain to your editorial standards, ' +
                        'in your own words. It is available once Pushly AI is on for this domain. ' +
                        (isInternalUser
                            ? 'Turn on Pushly AI for this domain to set it up.'
                            : 'Ask your Pushly account manager how Pushly AI can work for your content.')}{' '}
                    <a href={BRAND_VOICE_DOCS_URL} target="_blank" rel="noopener noreferrer">
                        Learn more
                    </a>
                </>
            }
        />
    </Well>
)

export const BrandVoiceSettings: FC<IBrandVoiceSettings> = ({ loading, domain }) => {
    const appState = useService(AppState)
    const enabled = domainHasFlag(domain, FEAT_PUSHLY_AI)
    const canEdit = !!domain && appState.abilityStore.can(AbilityAction.UPDATE, domain)

    return (
        <Flex vertical gap={VERTICAL_WELL_GAP} className={getClassNames('brand-voice-settings')}>
            {loading || !domain ? (
                <Well title="Brand Voice" footerMode="hidden">
                    <Skeleton active={true} title={false} />
                </Well>
            ) : enabled ? (
                <BrandVoiceWell domain={domain} canEdit={canEdit} />
            ) : (
                <BrandVoiceNeedsPushlyAi isInternalUser={!!appState.currentUser?.isInternalUser} />
            )}
        </Flex>
    )
}
